/**
 * SW 자동 업데이트 — 토스트 없이 "안전한 순간"에 조용히 적용 (ISSUE_063 후속)
 *
 * 배경: registerType:"prompt" 라 새 SW 는 waiting 상태로 대기만 하고 스스로 활성화하지
 * 않는다. 예전엔 onNeedRefresh 에서 "새로고침" 토스트를 띄웠는데 — 라벨과 달리 **브라우저
 * 수동 새로고침(Cmd+R)은 SW 업데이트를 적용하지 못한다**(대기 SW 는 그대로 남는다). 오직
 * skipWaiting 만 적용하므로, 습관적으로 수동 새로고침한 사용자에겐 토스트가 매 로드마다
 * 되살아나 "계속 뜨는데 안 먹는" 것처럼 보였다.
 *
 * 그래서 토스트를 없애고, skipWaiting+reload 시점을 우리가 **안전한 순간**으로 고른다:
 *  - 탭이 숨겨질 때(사용자가 떠남): 백그라운드에서 조용히 적용 → 돌아오면 최신
 *  - 앱 내 라우트 이동 시: 그 참에 적용 (PwaUpdater 가 applyUpdateOnNavigation 호출)
 * 탭이 활성이고 같은 화면에 머무는 동안(=편집 중 가능성)엔 건드리지 않아 갑작스런 리로드가 없다.
 *
 * 왜 registerType:"autoUpdate" 가 아닌가: autoUpdate 는 감지 즉시 리로드라 편집 중에도 튄다.
 * prompt + 수동 skipWaiting 으로 타이밍을 우리가 제어한다.
 */

/** vite-plugin-pwa registerSW 가 돌려주는 updateSW(reloadPage) */
let updateSWFn: ((reloadPage?: boolean) => Promise<void>) | null = null;
/** 새 SW 가 설치되어 대기 중인가 */
let updateReady = false;
/** 이미 적용(reload 트리거)했는가 — 중복 방지 */
let applied = false;

function applyUpdate() {
  if (applied || !updateReady || !updateSWFn) return;
  applied = true;
  void updateSWFn(true); // skipWaiting → controllerchange → location.reload()
}

/**
 * 모듈 부팅 시 1회 — SW 등록 + "탭 숨김" 안전-순간 트리거 배선.
 * PROD 브라우저에서만 동작(Tauri/DEV 는 무시).
 */
export function initSwUpdate(): void {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (!import.meta.env.PROD) return;
  if ("__TAURI_INTERNALS__" in window) return;

  void import("virtual:pwa-register").then(({ registerSW }) => {
    updateSWFn = registerSW({
      immediate: true,
      onNeedRefresh() {
        updateReady = true;
        // 이미 탭이 숨겨져 있으면(다른 탭·앱 사용 중) 지금 바로 백그라운드 적용
        if (document.visibilityState === "hidden") applyUpdate();
      },
    });
  });

  // 탭이 숨겨지는 순간 = 사용자가 떠남 → 백그라운드에서 안전하게 적용
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") applyUpdate();
  });
}

/** 앱 라우트가 바뀔 때 호출 — 대기 중 업데이트가 있으면 그 참에 적용한다. */
export function applyUpdateOnNavigation(): void {
  applyUpdate();
}
